import { useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import * as api from '@/lib/api';
import type * as T from '@/lib/types';
import { feedbackConversationKey, queryClient, refreshFeedbackConversation, type FeedbackConversationProjection } from '@/lib/queryClient';
import { captureFeedbackSend, captureFeedbackPendingAttempt } from '@/lib/feedbackConversationRecovery';
import { feedbackProseLength } from '@/lib/feedbackAuthoringContinuation';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

type Options = { scope: T.FeedbackConversationScope; target: T.FeedbackConversationTarget; enabled: boolean };
type Loaded = { current: T.FeedbackConversationResponse; transcript: T.FeedbackTranscriptResponse; read: T.FeedbackConversationReadState };
type Writing = T.FeedbackWritingState;
type Send = ReturnType<typeof captureFeedbackSend>;
const code = (error: unknown) => error instanceof api.ApiError && typeof error.detail === 'object' && error.detail !== null && 'code' in error.detail
  ? String(error.detail.code) : '';
// These named responses reject a non-send command. Missing/retired/unknown
// receipts and transport errors never establish rejection or unlock an intent.
const knownRejection = new Set(['feedback_version_conflict', 'conversation_closed', 'conversation_closed_required',
  'conversation_lifecycle_conflict', 'conversation_request_conflict', 'writing_version_conflict', 'writing_base_unavailable',
  'writing_base_conflict', 'writing_content_conflict', 'served_content_proof_required', 'conversation_request_invalid',
  'conversation_read_only', 'conversation_coach_required', 'actual_player_review_required', 'family_own_read_required']);

/** Render coordination only. Typed commands remain immutable until acknowledged;
 * the existing shared recovery controller owns every send disposition. */
export function useFeedbackConversation({ scope, target, enabled }: Options) {
  const boundary = useMemo(() => {
    const fingerprint = api.feedbackConversationAuthorityFingerprint(); let departed = false;
    const current = () => {
      if (!api.isCurrentFeedbackConversationScope(scope) || api.feedbackConversationAuthorityFingerprint() !== fingerprint) departed = true;
      return !departed;
    };
    return { current, subscribe: (notify: () => void) => {
      const changed = () => { current(); notify(); };
      const offAuth = useAuthStore.subscribe(changed), offScope = useScopeStore.subscribe(changed), offToken = api.observeAuthTokenChanges(changed); changed();
      return () => { offAuth(); offScope(); offToken(); };
    } };
    // One exact actor/scope/target is mounted by the keyed panel entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const authorized = useSyncExternalStore(boundary.subscribe, boundary.current, () => false);
  const [, render] = useReducer(n => n + 1, 0);
  const [loaded, setLoaded] = useState<Loaded | null>(null), loadedRef = useRef<Loaded | null>(null);
  const [loading, setLoading] = useState(true), [denied, setDenied] = useState(false);
  const [writingConflict, setWritingConflict] = useState(false);
  const [error, setError] = useState<string | null>(null), [busy, setBusy] = useState(false);
  const [text, setText] = useState(''), textRef = useRef(''), savedText = useRef<string | null>(null);
  const writing = useRef<Writing | null>(null), changed = useRef(false), autoSaveBlocked = useRef(false), admitted = useRef(false);
  const command = useRef<{ kind: 'writing-save' | 'transition'; purpose: 'writing-save' | 'other'; label: string; accepted: boolean; run: () => Promise<void> } | null>(null);
  const send = useRef<Send | null>(null), sendText = useRef<string | null>(null);
  const [answered, setAnswered] = useState<T.FeedbackAnsweredContextResponse | null>(null);
  const [fullView, setFullView] = useState<T.FeedbackFullViewResponse | null>(null);
  const [proof, setProof] = useState<T.FeedbackTranscriptViewResponse | null>(null);
  const readKeys = useRef(new Map<string, ReturnType<typeof feedbackConversationKey>>());
  const [cursor, setCursor] = useState<string | null>(null), cursorRef = useRef<string | null>(null);
  const [proofPage, setProofPage] = useState(false);
  const alive = useRef(true), actionAbort = useRef(new AbortController()), readGeneration = useRef(0);
  const running = useRef(false), fullPending = useRef(false), transcriptPending = useRef(false);
  const live = useRef({ enabled, authorized, denied }); live.current = { enabled, authorized, denied };
  if (!enabled || !authorized) admitted.current = false;
  const current = () => alive.current && live.current.enabled && live.current.authorized && !live.current.denied && boundary.current();
  const options = () => ({ scope, signal: actionAbort.current.signal });
  function captureError(value: unknown) {
    if (api.isFeedbackConversationContentUnavailable(value)) { setDenied(true); loadedRef.current = null; setLoaded(null); setAnswered(null); setFullView(null); setProof(null); }
  }
  function useWriting(value: Writing, replaceText = false) {
    writing.current = value; savedText.current = value.text;
    if (replaceText) { textRef.current = value.text; setText(value.text); changed.current = false; }
    render();
  }
  function query<V>(projection: FeedbackConversationProjection, fetcher: (signal: AbortSignal) => Promise<V>, selection: T.FeedbackTranscriptQuery & { messageId?: string } = {}) {
    const key = feedbackConversationKey(scope, target, projection, selection); readKeys.current.set(JSON.stringify(key), key);
    return queryClient.fetchQuery({ queryKey: key, staleTime: 0, queryFn: ({ signal }) => fetcher(signal) });
  }
  function cancelReads() {
    for (const queryKey of readKeys.current.values()) void queryClient.cancelQueries({ queryKey, exact: true }, { revert: false });
  }
  async function load() {
    if (!current()) return;
    const generation = ++readGeneration.current;
    setLoading(!admitted.current);
    try {
      const child = await query('child', signal => api.getFeedbackConversation(target, { scope, signal }));
      if (!current() || generation !== readGeneration.current) return;
      const selection = { limit: 20, cursor: cursorRef.current };
      const [transcript, read, privateWriting, pending] = await Promise.all([
        query('messages', signal => api.getFeedbackMessages(target, selection, { scope, signal }), selection),
        query('read-state', signal => api.getFeedbackConversationReadState(target, { scope, signal })),
        child.actor_role === 'family' ? null : api.getFeedbackWriting(target, options()),
        child.actor_role === 'family' ? null : api.getFeedbackPendingSend(target, options()),
      ]);
      if (!current() || generation !== readGeneration.current) return;
      if (child.feedback_revision_id !== transcript.feedback_revision_id || child.feedback_revision_id !== read.feedback_revision_id
        || child.lifecycle_version !== transcript.lifecycle_version) {
        admitted.current = false; setError('Feedback changed while opening. Refresh to read one current version.'); return;
      }
      admitted.current = true;
      loadedRef.current = { current: child, transcript, read }; setLoaded(loadedRef.current);
      setFullView(old => old?.feedback_revision_id === child.feedback_revision_id ? old : null);
      if (privateWriting && (!writing.current || !changed.current && !command.current && !send.current)) useWriting(privateWriting, true);
      if (pending?.pending && !send.current && !pending.pending.phase.endsWith('_resolved')) {
        send.current = captureFeedbackPendingAttempt(scope, target, { schema_version: 'conversation-pending-attempt.v1',
          pending_send_id: pending.pending.pending_send_id, expected_attempt_version: pending.pending.attempt_version });
        // An occupied descriptor can omit the receipt. Only original execution
        // through the shared helper proves its outcome; mount never dispatches it.
        render();
      }
    } catch (value) {
      if (current() && generation === readGeneration.current) { admitted.current = false; captureError(value); setError(code(value) === 'conversation_query_refresh_required'
        ? 'This conversation changed. Refresh its messages.' : 'Could not open this conversation. Your private writing is kept.'); }
    } finally { if (alive.current && generation === readGeneration.current) setLoading(false); }
  }
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; readGeneration.current++; actionAbort.current.abort(); cancelReads(); send.current?.dispose(); };
  }, []);
  useEffect(() => {
    if (!enabled || !authorized) { readGeneration.current++; actionAbort.current.abort(); cancelReads(); setFullView(null); setProof(null); return; }
    actionAbort.current = new AbortController(); void load();
    // Activation performs fresh authorization. It never sends or acknowledges.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, authorized]);

  async function retryCommand() {
    if (!current() || running.current || !command.current) return false;
    running.current = true; setBusy(true); setError(null);
    try {
      await command.current.run();
      if (!current()) return false;
      command.current = null; render(); await load(); return true;
    } catch (value) {
      if (current()) {
        captureError(value);
        autoSaveBlocked.current = true;
        if (value instanceof api.ApiError && knownRejection.has(code(value))) {
          if (['writing_version_conflict', 'writing_base_conflict', 'writing_base_unavailable'].includes(code(value))) setWritingConflict(true);
          command.current = null;
          setError('That change was not saved. Read the current Feedback and saved writing before trying again.');
        } else if (command.current?.purpose === 'writing-save') {
          setError(command.current.accepted ? 'Your last save worked, but we couldn’t load it. Retry the save before sending.'
            : 'We couldn’t confirm your draft was saved. Retry the save before sending.');
        } else setError(command.current?.accepted ? 'The change was accepted. Check the original operation to finish opening its saved result.' : 'The result is not confirmed. Check the original operation before making another change.');
      }
      return false;
    } finally { running.current = false; if (alive.current) setBusy(false); }
  }
  async function start(label: string, run: () => Promise<void>, kind: 'writing-save' | 'transition' = 'transition', purpose: 'writing-save' | 'other' = 'other') {
    if (!current() || running.current || command.current || send.current) return false;
    command.current = { kind, purpose, label, accepted: false, run }; render(); return retryCommand();
  }
  async function saveWriting(lockText = false) {
    const base = writing.current;
    if (!current() || !base || writingConflict || !changed.current || textRef.current === savedText.current || feedbackProseLength(textRef.current) > 1000
      || command.current || send.current || running.current) return false;
    const capturedText = textRef.current;
    const body: T.SaveFeedbackWritingRequest = { schema_version: 'conversation-writing.v1', operation_id: crypto.randomUUID(),
      expected_draft_version: base.draft_version, base_feedback_revision_id: base.base_feedback_revision_id,
      base_lifecycle_version: base.base_lifecycle_version, text: capturedText, quick_label: base.quick_label };
    return start('Retry save', async () => {
      const receipt = await api.saveFeedbackWriting(target, body, options());
      if (!current()) return;
      if (command.current) command.current.accepted = true;
      // Accepted version comes only from this operation's acknowledgement. A
      // newer GET cannot silently replace the captured writing CAS.
      writing.current = { ...base, writing_id: receipt.writing_id, conversation_id: receipt.conversation_id,
        draft_version: receipt.version, text: capturedText };
      const canonical = await api.getFeedbackWriting(target, options());
      if (!current()) return;
      if (canonical.writing_id === receipt.writing_id && canonical.draft_version === receipt.version) {
        useWriting(canonical, textRef.current === capturedText);
      } else { savedText.current = null; setError('Writing was saved. It has since changed elsewhere; your local reply is kept.'); }
    }, lockText ? 'transition' : 'writing-save', 'writing-save');
  }
  useEffect(() => {
    if (!enabled || !authorized || autoSaveBlocked.current || !changed.current || command.current || send.current || busy) return;
    const timer = window.setTimeout(() => { if (!autoSaveBlocked.current) void saveWriting(); }, 600);
    return () => window.clearTimeout(timer);
    // Autosave follows user edits only; initial or restored reads never write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, enabled, authorized, busy]);
  function editText(value: string) {
    if (!current() || send.current || command.current && command.current.kind !== 'writing-save') return;
    autoSaveBlocked.current = false; textRef.current = value; changed.current = true; setText(value);
  }
  async function discardWriting() {
    const base = writing.current; if (!base || !current() || command.current || send.current || running.current) return;
    if (!base.writing_id) { useWriting({ ...base, text: '', quick_label: null }, true); return; }
    const body: T.DiscardFeedbackWritingRequest = { schema_version: 'conversation-writing-discard.v1', operation_id: crypto.randomUUID(), expected_draft_version: base.draft_version };
    await start('Check discarded reply', async () => {
      const receipt = await api.discardFeedbackWriting(target, body, options()); if (!current()) return;
      useWriting({ ...base, draft_version: receipt.version, text: '', quick_label: null }, true);
    });
  }
  async function selectCurrent() {
    if (!current() || running.current || command.current || send.current) return;
    // Explicit selection may read current CAS; background refresh never does.
    const base = await api.getFeedbackWriting(target, options()).catch(value => { if (current()) { captureError(value); setError('Could not open saved writing. Your reply is kept.'); } return null; });
    const child = loadedRef.current?.current;
    if (!base || !child || !current()) return;
    const body: T.SelectFeedbackCurrentRequest = { schema_version: 'conversation-selection.v1', operation_id: crypto.randomUUID(),
      expected_draft_version: base.draft_version, expected_feedback_revision_id: child.feedback_revision_id, expected_lifecycle_version: child.lifecycle_version };
    await start('Check current selection', async () => {
      const receipt = await api.selectFeedbackCurrent(target, body, options()); if (!current()) return;
      writing.current = { ...base, draft_version: receipt.version, writing_id: receipt.writing_id, conversation_id: receipt.conversation_id,
        base_feedback_revision_id: child.feedback_revision_id, base_lifecycle_version: child.lifecycle_version, base_available: true };
      // Selecting current context retains any locally edited prose.
      setWritingConflict(false); autoSaveBlocked.current = false; savedText.current = base.text; changed.current = textRef.current !== base.text; setAnswered(null); render();
    });
  }
  async function runSend() {
    const controller = send.current; if (!current() || !controller || running.current || command.current) return;
    running.current = true; setBusy(true); setError(null);
    try {
      const result = await controller.send(actionAbort.current.signal); if (!current()) return;
      if (result.phase.startsWith('accepted')) {
        setProof(null); setProofPage(false);
        if (result.message?.writing_disposition === 'consumed' && textRef.current === sendText.current) {
          textRef.current = ''; setText(''); changed.current = false;
        }
        await finishSend(controller); return;
      } else if (result.phase.startsWith('rejected')) setError('This reply was not sent. Keep your writing and read the current Feedback before trying again.');
      await load();
    } catch (value) { if (current()) { captureError(value); setError('Send result unknown. Check this original send; your writing is kept.'); } }
    finally { running.current = false; if (alive.current) { setBusy(false); render(); } }
  }
  async function sendReply(label?: T.FeedbackConversationMessage['quick_label']) {
    const child = loadedRef.current?.current;
    if (!current() || !child || child.actor_role === 'family' || child.closed || answered || command.current || send.current || running.current
      || writingConflict || feedbackProseLength(textRef.current) > 1000 || !label && feedbackProseLength(textRef.current) === 0) return;
    // Save user prose first. A lost save acknowledgement cannot become a send.
    if (changed.current && textRef.current !== savedText.current && !await saveWriting(true)) return;
    const base = writing.current;
    if (!current() || !base || !base.base_available || base.base_feedback_revision_id !== child.feedback_revision_id
      || base.base_lifecycle_version !== child.lifecycle_version || command.current || running.current) return;
    const body: T.AppendFeedbackMessageRequest = { schema_version: 'conversation-append.v1', operation_id: crypto.randomUUID(),
      expected_feedback_revision_id: base.base_feedback_revision_id, expected_lifecycle_version: base.base_lifecycle_version,
      writing_id: base.writing_id, expected_draft_version: base.draft_version, writing_mode: label ? 'preserve' : 'consume',
      text: label ? '' : textRef.current, quick_label: label ?? null };
    sendText.current = body.text;
    send.current = captureFeedbackSend(scope, target, { schema_version: 'conversation-send-reservation.v1', command: body, retry: null });
    render(); await runSend();
  }
  async function finishSend(controller: Send) {
    try {
      await controller.resolve(actionAbort.current.signal); if (!current()) return;
      setError(null);
      // Only the proved disposition releases the slot; fresh own writing then
      // supplies the next permitted CAS. Failed hydration keeps local prose.
      const value = await api.getFeedbackWriting(target, options());
      if (current()) { useWriting(value, !changed.current); controller.dispose(); send.current = null; sendText.current = null; await load(); }
    } catch (value) { if (current()) { captureError(value); setError(controller.getState().status.endsWith('_resolved') ? 'Send outcome confirmed. Continue to open your saved writing.' : 'Send outcome confirmed. Could not finish checking this send. Retry the same operation.'); } }
  }
  async function recoverSend() {
    const controller = send.current; if (!controller || !current() || running.current) return;
    const state = controller.getState();
    // A lost resolution ACK returns the helper to unknown. Re-execute only its
    // original attempt to prove the disposition before resolving it again.
    if (!['accepted', 'rejected', 'accepted_resolved', 'rejected_resolved'].includes(state.status)) { await runSend(); return; }
    running.current = true; setBusy(true);
    try { await finishSend(controller); }
    finally { running.current = false; if (alive.current) { setBusy(false); render(); } }
  }
  async function lifecycle(action: 'close' | 'reopen' | 'request') {
    const child = loadedRef.current?.current; if (!child || child.actor_role === 'family') return;
    const common: T.FeedbackLifecycleRequest = { schema_version: 'conversation-lifecycle.v1', operation_id: crypto.randomUUID(), expected_lifecycle_version: child.lifecycle_version };
    const request: T.FeedbackReopeningRequest = { schema_version: 'conversation-reopening.v1', operation_id: common.operation_id,
      expected_lifecycle_version: child.lifecycle_version, expected_request_version: child.request_version };
    await start('Check conversation change', async () => {
      if (action === 'request') await api.requestFeedbackReopening(target, request, options());
      else if (action === 'close') await api.closeFeedbackConversation(target, common, options());
      else await api.reopenFeedbackConversation(target, common, options());
    });
  }
  async function reviewCurrent() {
    const child = loadedRef.current?.current; if (!child || child.actor_role !== 'player' || answered) return;
    const body: T.FeedbackCurrentReviewRequest = { schema_version: 'conversation-current-review.v1', operation_id: crypto.randomUUID(), expected_feedback_revision_id: child.feedback_revision_id };
    await start('Check current review', async () => { await api.markFeedbackCurrentReviewed(target, body, options()); });
  }
  async function prepareFullView() {
    const child = loadedRef.current?.current;
    if (!current() || child?.actor_role !== 'family' || fullView || fullPending.current || answered) return;
    fullPending.current = true;
    try {
      const value = await api.serveFeedbackFullView(target, { schema_version: 'feedback-full-view.v1' }, options());
      if (current() && loadedRef.current?.current.feedback_revision_id === value.feedback_revision_id) setFullView(value);
    } catch (value) { if (current()) { captureError(value); setError('Could not open the full current Feedback. Reading has not been recorded.'); } }
    finally { fullPending.current = false; }
  }
  async function recordOwnReading(isVisible: () => boolean) {
    if (!fullView || answered || !isVisible() || !current() || loadedRef.current?.read.actor_role !== 'family'
      || loadedRef.current.read.read_for_me || command.current || running.current) return;
    const body: T.FeedbackOwnReadRequest = { schema_version: 'feedback-own-read.v1', operation_id: crypto.randomUUID(),
      expected_feedback_revision_id: fullView.feedback_revision_id, served_content_proof: fullView.served_content_proof };
    await start('Check own reading', async () => { if (!isVisible()) throw new DOMException('Content is not visible', 'AbortError');
      await api.markFeedbackOwnReading(target, body, options()); });
  }
  async function prepareTranscript() {
    if (!current() || transcriptPending.current || proof) return;
    transcriptPending.current = true;
    try {
      const value = await api.serveFeedbackTranscript(target, { schema_version: 'conversation-transcript-view.v1', limit: 20 }, options());
      if (current()) { setProof(value); if (value.messages.length) setProofPage(true); }
    } catch (value) { if (current()) { captureError(value); setError('Could not open unread messages. No messages were marked seen.'); } }
    finally { transcriptPending.current = false; }
  }
  async function markSeen(isVisible: () => boolean) {
    if (!current() || !proofPage || !proof?.served_content_proof || !isVisible() || !proof.messages.length) return;
    const body: T.FeedbackSeenRequest = { schema_version: 'conversation-seen.v1', operation_id: crypto.randomUUID(),
      seen_up_to_sequence: proof.covered_through_sequence, served_content_proof: proof.served_content_proof };
    await start('Check messages seen', async () => { if (!isVisible()) throw new DOMException('Messages are not visible', 'AbortError');
      await api.markFeedbackConversationSeen(target, body, options()); if (current()) { setProof(null); setProofPage(false); } });
  }
  async function openAnswered(message: T.FeedbackConversationMessage) {
    if (!current()) return;
    const generation = readGeneration.current;
    try { const value = await query('answered-context', signal => api.getFeedbackAnsweredContext(target, message.id, { scope, signal }), { messageId: message.id });
      if (current() && generation === readGeneration.current) setAnswered(value);
    } catch (value) { if (current()) { captureError(value); setError('This answered Feedback is unavailable. Your writing is kept.'); } }
  }
  function page(next: string | null) { cursorRef.current = next; setCursor(next); setProofPage(false); void load(); }
  const sendState = send.current?.getState();
  const recoveryLabel = command.current?.label ?? (send.current ? sendState?.status === 'accepted' || sendState?.status === 'accepted_resolved' ? 'Continue after sent reply'
    : sendState?.status === 'rejected' ? 'Keep writing' : 'Check send result' : undefined);
  return { authorized, denied, loading: loading || !admitted.current, loaded, text, answered, fullView, proof, proofPage, cursor, error, busy,
    pending: !!command.current || !!send.current, saved: savedText.current !== null && text === savedText.current,
    // Purpose controls copy only: the explicit pre-send save still locks text
    // as a transition, but it must not be described as an uncertain send.
    draftSavePending: command.current?.purpose === 'writing-save',
    // A save owns its captured operation/CAS, not the editable field. Disabling
    // the focused textarea on the 600ms autosave drops native focus/caret.
    // Later typing remains local until that original save is resolved. Sends
    // and destructive/context-changing commands retain the existing text lock.
    writingLocked: !!send.current || !!command.current && command.current.kind !== 'writing-save',
    writingConflict, stale: writingConflict || !!answered || !!writing.current && !!loaded && (!writing.current.base_available
      || writing.current.base_feedback_revision_id !== loaded.current.feedback_revision_id || writing.current.base_lifecycle_version !== loaded.current.lifecycle_version),
    editText, saveWriting, discardWriting, sendReply, selectCurrent, lifecycle, reviewCurrent, prepareFullView, recordOwnReading,
    prepareTranscript, markSeen, openAnswered, page, recoveryLabel,
    recover: () => command.current ? retryCommand() : recoverSend(),
    showCurrent: () => setAnswered(null),
    refresh: async () => { setError(null); setFullView(null); setProof(null); setProofPage(false); cursorRef.current = null; setCursor(null);
      await refreshFeedbackConversation(scope, target); await load(); },
  };
}
